/**
 * Staging-only visual chrome ([STG-CHROME-1]).
 *
 * Hostname is the production lock: known production hosts NEVER show
 * chrome, even if this bundle is later promoted via staging → main or
 * a build was compiled with VITE_APP_ENV=staging.
 *
 * VITE_APP_ENV=staging is only a local/preview override, and only on
 * hosts that are not production.
 */

export const PRODUCTION_HOSTS = [
  'app.larkpm.com',
  'larkpm.com',
  'www.larkpm.com',
  'admin.larkpm.com',
] as const;

export const STAGING_DOCUMENT_TITLE_PREFIX = 'STAGING · ';

export type StagingEnvironmentInput = {
  hostname?: string;
  appEnv?: string;
};

export function normalizeHostname(hostname: string): string {
  return hostname.trim().toLowerCase().replace(/\.$/, '');
}

export function readViteAppEnv(): string {
  try {
    return String(import.meta.env.VITE_APP_ENV ?? '');
  } catch {
    return '';
  }
}

export function isProductionHostname(hostname: string): boolean {
  return (PRODUCTION_HOSTS as readonly string[]).includes(normalizeHostname(hostname));
}

export function isStagingHostname(hostname: string): boolean {
  const host = normalizeHostname(hostname);
  if (!host || isProductionHostname(host)) return false;
  if (host === 'staging.larkpm.com') return true;
  if (host.endsWith('.staging.larkpm.com')) return true;
  return /^staging[-.].+\.larkpm\.com$/.test(host);
}

export function isStagingEnvironment(input: StagingEnvironmentInput = {}): boolean {
  const hostname =
    input.hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  if (isProductionHostname(hostname)) return false;
  if (isStagingHostname(hostname)) return true;
  const env = (input.appEnv ?? readViteAppEnv()).trim().toLowerCase();
  return env === 'staging';
}

export function stagingDocumentTitle(currentTitle: string): string {
  if (currentTitle.startsWith(STAGING_DOCUMENT_TITLE_PREFIX)) return currentTitle;
  const trimmed = currentTitle.trim();
  return `${STAGING_DOCUMENT_TITLE_PREFIX}${trimmed || 'LarkPM'}`;
}

export function buildStagingFaviconDataUrl(): string | null {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = 32;
  canvas.height = 32;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = '#f59e0b';
  ctx.fillRect(0, 0, 32, 32);
  ctx.fillStyle = '#451a03';
  ctx.font = 'bold 22px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('S', 16, 18);
  return canvas.toDataURL('image/png');
}

export function applyStagingDocumentChrome(input: StagingEnvironmentInput = {}): void {
  if (typeof document === 'undefined') return;
  if (!isStagingEnvironment(input)) return;

  document.documentElement.classList.add('larkpm-staging');
  document.title = stagingDocumentTitle(document.title);

  const href = buildStagingFaviconDataUrl();
  if (!href) return;
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.type = 'image/png';
  link.href = href;
}
